import { useRef, Fragment, createRef, useLayoutEffect } from 'react'
import classNames from 'classnames'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { useSelector, shallowEqual } from 'react-redux'
import { createUseStyles } from 'react-jss'
import DelayLink from '@/components/DelayLink'
import useScrollTrigger from '@/hooks/useScrollTrigger'
import { decodeEntities } from '@/utils/decodeEntities'
import style from './style'

gsap.registerPlugin(ScrollTrigger)
const useStyles = createUseStyles(style)

const ListHover = () => {
  const trigger = useRef([])
  const $root = useRef()
  const $follow = useRef()
  const classes = useStyles()
  const { productCategories } = useSelector((state) => ({
    productCategories: state.page.taxonomies._cta_category_product || [], // eslint-disable-line no-underscore-dangle
    mediaQueryKey: state.mediaQuery.key,
  }), shallowEqual)
  const $titles = useRef(productCategories.map(() => createRef()))
  const $lines = useRef(productCategories.map(() => createRef()))
  const { animationReady } = useScrollTrigger()

  useLayoutEffect(() => {
    if (animationReady) {
      $titles.current.map((title) => (
        trigger.current.push(ScrollTrigger.create({
          trigger: title.current,
          onEnter: () => gsap.to(title.current, { y: 0, opacity: 1 }),
        }))
      ))
      $lines.current.filter((l) => l.current !== null).map((line) => (
        gsap.from(line.current, {
          scrollTrigger: {
            trigger: line.current,
            scrub: true,
            start: 'top bottom',
            end: 'top center',
            once: false,
          },
          scaleX: 0,
          transformOrigin: 'left center',
          ease: 'none',
        })
      ))
    }
    return () => {
      if (trigger.current.length > 0) trigger.current.map((tr) => tr.kill())
    }
  }, [animationReady])

  const defaultOpacity = 0.75

  const handleMouseMove = (e) => {
    const bounds = $root.current.getBoundingClientRect()
    gsap.to($follow.current, {
      duration: 0.4,
      y: e.clientY - bounds.top,
      x: e.clientX - bounds.left,
    })
  }

  const handleMouseEnter = (e, id) => {
    const bounds = $root.current.getBoundingClientRect()
    const target = e.currentTarget
    const allItems = $root.current.querySelectorAll('.navItem')
    const allDividers = $root.current.querySelectorAll('.divider')
    const allImages = $follow.current.querySelectorAll('img')
    const img = $follow.current.querySelector(`[data-img="${id}"]`)

    gsap.to([allItems, allDividers], {
      duration: 0.3,
      opacity: defaultOpacity,
    })
    gsap.to(target, {
      duration: 0.3,
      opacity: 1,
    })

    gsap.to(allImages, {
      duration: 0.5,
      opacity: 0,
      ease: 'power3.out',
    })
    gsap.to(img, {
      duration: 0.5,
      opacity: 0.6,
      ease: 'power3.out',
    })

    gsap.to($follow.current, {
      duration: 0.3,
      opacity: 1,
    })

    gsap.to($follow.current, {
      duration: 0.4,
      y: e.clientY - bounds.top,
      x: e.clientX - bounds.left,
    })
  }

  const handleMouseLeave = () => {
    const allItems = $root.current.querySelectorAll('.navItem')
    const allDividers = $root.current.querySelectorAll('.divider')
    gsap.to([allItems, allDividers], {
      duration: 0.3,
      opacity: defaultOpacity,
    })
    gsap.to($follow.current, {
      duration: 0.3,
      opacity: 0,
    })
  }

  return (
    <>
      <div
        className={classNames({
          [classes.menuWrapper]: true,
          productHover__menuWrapper: true,
        })}
        onMouseMove={handleMouseMove}
        ref={$root}
      >
        <div className={classes.follow} ref={$follow}>
          {productCategories.length > 0 && productCategories
            .filter((category) => category.count > 0)
            .map((item, index) => {
              return item?.meta?.taxonomy_list_image_url && (
                <img
                  className={classes.followImg}
                  data-img={item.id}
                  key={index.toString()}
                  src={item?.meta?.taxonomy_list_image_url}
                  alt={item.name}
                />
              )
            })}
        </div>
        <div className={classes.menuWrapper}>
          {productCategories.length > 0
          && productCategories
            .filter((category) => category.count > 0)
            .map((it) => ({
              id: it.id,
              link: it?.meta?.taxonomy_external_url || it.link,
              title: it.name,
            }))
            .map((item, i) => (
              <Fragment key={item.id}>
                <div
                  className={classNames({
                    productHover__navItem: true,
                    [classes.navItemWrapper]: true,
                    productHover__navItemWrapper: true,
                  })}
                >
                  <DelayLink
                    to={item.link}
                    attribute={decodeEntities(item.title)}
                    onMouseEnter={(e) => handleMouseEnter(e, item.id)}
                    onMouseLeave={handleMouseLeave}
                  >
                    <div className={classes.titleWrapper}>
                      <span
                        ref={$titles.current[i]}
                        data-text={item.title}
                      >
                        {item.title}
                      </span>
                    </div>
                  </DelayLink>

                </div>
                {((i + 1) !== productCategories.length) && (
                  <span
                    ref={$lines.current[i]}
                    className={classes.line}
                  />
                )}
              </Fragment>
            ))}
        </div>
      </div>
    </>
  )
}

export default ListHover
